import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ApiClient } from '../api';
import { BRAND_META, eventLabel, marketWords } from '../brand';
import { money, timeAgo } from '../format';
import type { EventDetail, FeedEvent } from '../types';

/**
 * One live event in full (Matthew, 11 October 2026: "tap/click it and find
 * all the information about the transaction. Similar to being able to
 * click and get info on all the activity"). Opens from any feed row and
 * shows everything the read model holds about it (GET /api/event): what,
 * when, how much, cashback, where (country, and city only where the atlas
 * threshold allows), which offer, creator, brand or merchant, the channel
 * and app, and its later history such as a confirmation or reversal.
 *
 * What the platforms do not send cannot appear here, and the panel says
 * so rather than leaving a gap: an in-store purchase from a CashBlack
 * publisher older than 0.7.0 carries no stall or event name, for instance.
 */

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/London',
  });

const PLATFORM: Record<string, string> = { web: 'Website', ios: 'iPhone app', android: 'Android app', extension: 'A.F.R.O.B.O.T extension' };

type Row = [string, React.ReactNode];

export function detailRows(d: EventDetail): Row[] {
  const rows: Row[] = [];
  const m = (minor: number | null) => (minor === null || !d.currency ? null : money(minor, d.currency));
  const add = (label: string, value: React.ReactNode | null | undefined) => {
    if (value !== null && value !== undefined && value !== '') rows.push([label, value]);
  };
  const sub = d.event_type.startsWith('subscription.');
  add(sub ? 'Monthly value' : 'Amount', m(d.amount_minor));
  add('Plan', d.plan_name);
  add('Billed', d.plan_amount_minor !== null && d.interval ? `${m(d.plan_amount_minor)} a ${d.interval}` : null);
  add('Cashback to the member', m(d.cashback_minor));
  add('Our revenue', m(d.revenue_minor));
  add('Member gave', m(d.member_minor));
  add('Matched', m(d.match_minor));
  add('Discount', m(d.discount_minor));
  add(d.event_type === 'member.joined' ? 'Member' : d.event_type.startsWith('support.') ? 'From' : 'Bought by', d.member_name);
  add('Referral code', d.referral_code);
  add('Priority', d.priority ? d.priority[0]!.toUpperCase() + d.priority.slice(1) : null);
  add('Merchant', d.names.merchant ?? (d.merchant_id ? `id ${d.merchant_id}` : null));
  add('Stall', d.stall_name);
  add('Event', d.event_name);
  add('Offer', d.names.offer ?? (d.offer_id ? `id ${d.offer_id}` : null));
  add('Creator', d.names.creator ?? (d.creator_id ? `id ${d.creator_id}` : null));
  add('Brand', d.names.advertiser ?? (d.advertiser_id ? `id ${d.advertiser_id}` : null));
  add(d.event_type === 'app.first_opened' ? 'App' : 'Site', d.market ? marketWords(d) : null);
  add('App version', d.app_version);
  add('Channel', d.channel === 'irl' ? 'In store (IRL)' : d.event_type.startsWith('transaction') && d.brand === 'cashblack' ? 'Online' : d.channel);
  add('Through', d.platform ? (PLATFORM[d.platform] ?? d.platform) : null);
  add('Category', d.category);
  add('Seats', d.seat_count);
  add('Where', [d.city, d.country].filter(Boolean).join(', ') || null);
  add('Happened', fmtTime(d.occurred_at));
  add('Reached the dashboard', `${fmtTime(d.received_at)}`);
  return rows;
}

/** A plain-words note for what the platform did not send. */
export function missingNote(d: EventDetail): string | null {
  if (d.brand === 'cashblack' && d.channel === 'irl' && !d.stall_name) {
    return 'This in-store purchase arrived without the stall or event name, so who it was bought from cannot be shown. CashBlack has been asked to send both.';
  }
  return null;
}

export function EventDetailSheet({
  api,
  event,
  onClose,
  onDeepLink,
}: {
  api: ApiClient;
  event: FeedEvent;
  onClose: () => void;
  onDeepLink?: (e: FeedEvent) => void;
}) {
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const params = new URLSearchParams({ id: event.event_id, at: event.occurred_at });
    api
      .get<{ event: EventDetail }>(`/api/event?${params}`)
      .then((r) => live && setDetail(r.event))
      .catch((err) => live && setError(/404/.test(String(err)) ? 'This event is no longer held (raw events are kept 90 days).' : 'Could not load this event.'));
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      live = false;
      window.removeEventListener('keydown', onKey);
    };
  }, [api, event, onClose]);

  const meta = BRAND_META[event.brand];
  const hasEntity = !!(event.offer_id ?? event.creator_id ?? event.merchant_id ?? event.advertiser_id);
  const note = detail ? missingNote(detail) : null;

  return createPortal(
    <div className="cc-sheet-backdrop" onClick={onClose}>
      <section
        className="cc-sheet cc-event"
        role="dialog"
        aria-label={`${meta.label} ${eventLabel(event.event_type)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h3>
            <span className="pip" style={{ background: meta.series }} aria-hidden /> {meta.label} ·{' '}
            {eventLabel(event.event_type)}
          </h3>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </header>
        <p className="cc-sheet-note">{timeAgo(event.occurred_at)}</p>
        {error && <p className="cc-sheet-note">{error}</p>}
        {!detail && !error && <p className="cc-sheet-note">Loading…</p>}
        {detail && (
          <>
            <dl className="cc-event-rows">
              {detailRows(detail).map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
            {detail.trail.length > 0 && (
              <>
                <h4>What happened next</h4>
                <ul className="cc-event-trail">
                  {detail.trail.map((t) => (
                    <li key={`${t.event_type}${t.occurred_at}`}>
                      <strong>{eventLabel(t.event_type)}</strong>
                      {t.revenue_minor !== null && t.currency ? ` · our revenue ${money(t.revenue_minor, t.currency)}` : ''}
                      <span>{fmtTime(t.occurred_at)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {note && <p className="cc-sheet-note">{note}</p>}
          </>
        )}
        {onDeepLink && hasEntity && (
          <button type="button" className="cc-sheet-primary" onClick={() => onDeepLink(event)}>
            Open in the {meta.label} admin →
          </button>
        )}
      </section>
    </div>,
    document.body,
  );
}
